// src/utils/request.js
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { wsService } from '@/utils/websocket'

const baseURL = process.env.VUE_APP_API_BASE || '/api'

// ========== Token 管理 ==========

let isRefreshing = false
let refreshSubscribers = []
let refreshTimer = null
let heartbeatTimer = null

function parseJwtPayload(token) {
  try {
    const base64 = token.split('.')[1]
    const json = decodeURIComponent(atob(base64).split('').map(c =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''))
    return JSON.parse(json)
  } catch { return null }
}

function getTokenExpiry() {
  const token = localStorage.getItem('token')
  if (!token) return 0
  const payload = parseJwtPayload(token)
  return payload ? payload.exp * 1000 : 0
}

function isTokenExpiring() {
  const expiry = getTokenExpiry()
  if (!expiry) return false
  return expiry - Date.now() < 10 * 60 * 1000
}

async function refreshTokenSilently() {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) throw new Error('No refresh token')
  const res = await axios.post(`${baseURL}/auth/refresh`, { refreshToken }, {
    headers: { 'Content-Type': 'application/json' }
  })
  if (res.data.code === 200) {
    const { accessToken, refreshToken: newRefreshToken } = res.data.data
    localStorage.setItem('token', accessToken)
    if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken)
    // 广播给其他标签页
    try { localStorage.setItem('_tokenRefreshed', String(Date.now())) } catch {}
    setupTokenRefresh()
    console.log('[Token] 刷新成功')

    // ★ FIX: token 刷新成功，用新 token 重连 WebSocket
    try { wsService.reconnectWithToken(accessToken) } catch {}

    return res.data.data
  }
  throw new Error(res.data.message || 'Refresh failed')
}

function setupTokenRefresh() {
  if (refreshTimer) clearTimeout(refreshTimer)
  const expiry = getTokenExpiry()
  if (!expiry) return
  const timeUntilRefresh = expiry - Date.now() - 10 * 60 * 1000
  if (timeUntilRefresh > 0) {
    refreshTimer = setTimeout(async () => {
      try { await refreshTokenSilently() }
      catch (e) { console.warn('[Token] 定时刷新失败:', e.message) }
    }, timeUntilRefresh)
  } else {
    refreshTokenSilently().catch(() => {})
  }
}

function startHeartbeat() {
  if (heartbeatTimer) clearInterval(heartbeatTimer)
  heartbeatTimer = setInterval(async () => {
    const token = localStorage.getItem('token')
    if (!token) { stopHeartbeat(); return }
    if (isTokenExpiring()) {
      console.log('[Token] 心跳：token即将过期，刷新')
      try { await refreshTokenSilently() } catch (e) { console.warn('[Token] 心跳刷新失败:', e.message) }
    }
  }, 60000)
}

function stopHeartbeat() {
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null }
}

// 多标签页同步：监听其他标签页刷新成功事件
function setupStorageSync() {
  window.addEventListener('storage', (e) => {
    if (e.key === '_tokenRefreshed') {
      console.log('[Token] 检测到其他标签页刷新了token')
      setupTokenRefresh() // 重新设置本页定时器
      // ★ 用最新的 token 重连 WebSocket
      const newToken = localStorage.getItem('token')
      if (newToken) {
        try { wsService.reconnectWithToken(newToken) } catch {}
      }
    }
  })
}

// 页面从后台恢复时检查token状态（浏览器节流setInterval后兜底）
function setupVisibilityCheck() {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const token = localStorage.getItem('token')
      if (token && isTokenExpiring()) {
        console.log('[Token] 页面恢复，检测到token即将过期，刷新')
        refreshTokenSilently().catch(() => {})
      }
      // ★ 页面恢复时，如果 WebSocket 断开，尝试重连
      if (token && !wsService.connected && !wsService.connecting) {
        console.log('[WS] 页面恢复，尝试重连WebSocket')
        try { wsService.connect(token) } catch {}
      }
    }
  })
}

function onRefreshed(token) {
  refreshSubscribers.forEach(cb => cb(token))
  refreshSubscribers = []
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback)
}

function clearAuthAndRedirect() {
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('userInfo')
  localStorage.removeItem('roles')
  localStorage.removeItem('permissions')
  if (refreshTimer) clearTimeout(refreshTimer)
  stopHeartbeat()
  // ★ 登出时断开 WebSocket
  try { wsService.disconnect() } catch {}
  if (window.location.pathname !== '/login') window.location.replace('/login')
}

const instance = axios.create({
  baseURL, timeout: 30000,
  headers: { 'Content-Type': 'application/json;charset=UTF-8' }
})

instance.interceptors.request.use(async config => {
  const token = localStorage.getItem('token')
  if (!token) return config
  config.headers.Authorization = `Bearer ${token}`
  if (isTokenExpiring() && !isRefreshing) {
    try {
      const data = await refreshTokenSilently()
      if (data?.accessToken) config.headers.Authorization = `Bearer ${data.accessToken}`
    } catch {}
  }
  return config
})

instance.interceptors.response.use(
  response => {
    if (response.config?.responseType === 'blob') return response.data
    const responseData = response.data
    if (responseData.code === 200) return responseData
    const error = new Error(responseData.message || '业务逻辑错误')
    error.code = responseData.code
    error.data = responseData.data
    return Promise.reject(error)
  },
  async error => {
    const originalRequest = error.config
    const status = error.response?.status

    if (status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
        clearAuthAndRedirect()
        return Promise.reject(error)
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber(token => {
            if (token) { originalRequest.headers.Authorization = `Bearer ${token}`; resolve(instance(originalRequest)) }
            else reject(error)
          })
        })
      }
      originalRequest._retry = true
      isRefreshing = true
      try {
        const data = await refreshTokenSilently()
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`
        onRefreshed(data.accessToken)
        return instance(originalRequest)
      } catch {
        onRefreshed(null)
        setTimeout(clearAuthAndRedirect, 100)
        return Promise.reject(error)
      } finally {
        isRefreshing = false
        refreshSubscribers = []
      }
    }

    if (status === 429) {
      const retryAfter = error.response?.headers['retry-after'] || 60
      ElMessage.warning(`请求过于频繁，请${retryAfter}秒后再试`)
    } else if (status !== 401) {
      const message = error.response?.data?.message || { 400: '请求参数错误', 403: '没有访问权限', 404: '资源不存在', 500: '服务器内部错误' }[status] || error.message || '请求失败'
      ElMessage.error(message)
      error.friendlyMessage = message
    }
    return Promise.reject(error)
  }
)

export function initTokenRefresh() {
  setupTokenRefresh()
  startHeartbeat()
  setupStorageSync()
  setupVisibilityCheck()
}

export const http = {
  get: (url, config) => instance.get(url, config),
  post: (url, data, config) => instance.post(url, data, config),
  put: (url, data, config) => instance.put(url, data, config),
  patch: (url, data, config) => instance.patch(url, data, config),
  delete: (url, config) => instance.delete(url, config),
  download: (url, config) => instance.get(url, { ...config, responseType: 'blob' }),
  upload: (url, formData, config) => instance.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }, ...config
  }),
}